import { FormEvent, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { LicenseStatusBadge } from "@/components/LicenseStatusBadge"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface LicenseRow {
  id: number
  masked_key: string
  status: string
  account_id: number
  account_name: string
  assignee_email: string | null
  device_count: number
}

export default function AdminLicensesIndex({ licenses, q }: { licenses: LicenseRow[]; q: string }) {
  const [query, setQuery] = useState(q)

  const search = (e: FormEvent) => {
    e.preventDefault()
    router.get("/admin/licenses", query ? { q: query } : {}, { preserveState: true })
  }

  return (
    <>
      <Head title="Licenses">
        <meta name="description" content="All Flywrite license keys, their status, and devices." />
        <meta property="og:title" content="Licenses" />
        <meta property="og:description" content="All Flywrite license keys, their status, and devices." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Licenses"
          description={`${licenses.length} ${licenses.length === 1 ? "license" : "licenses"}${q ? ` matching “${q}”` : ""}.`}
        />

        <form onSubmit={search} className="mt-6 flex gap-2">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Paste a full license key or search by member email…"
            aria-label="Search licenses"
            className="max-w-sm"
          />
          <Button type="submit" variant="secondary">
            <Search className="h-4 w-4" />
            Search
          </Button>
        </form>

        {licenses.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No licenses found.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Key</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Account</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Assigned to</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Devices</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link href={`/admin/licenses/${license.id}`} className="font-mono text-xs">
                        {license.masked_key}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <LicenseStatusBadge status={license.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Link href={`/admin/accounts/${license.account_id}`}>{license.account_name}</Link>
                    </td>
                    <td className="px-4 py-3">{license.assignee_email ?? "Unassigned"}</td>
                    <td className="px-4 py-3 text-right">{license.device_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}
